import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import Toast from "@/components/ui/toast"
import { useUploadFileMutation } from "@/store/FileAPI"

interface UploadRequest {
  files: FormData
  type: string
}

interface UploadResponse {
  files: Array<{
    location: string
  }>
}

interface UseFileUploadProps {
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  value?: string | number | readonly string[] | undefined
  inReview?: boolean
  acceptedFileTypes?: string
  onInvalidFile?: () => void
  /**
   * When provided, the selected File is handed to the caller and the built-in
   * S3 upload is skipped. Use this when the parent owns the upload pipeline.
   */
  onFileSelect?: (file: File) => void
}

function isFileAccepted(file: File, accept: string): boolean {
  return accept.split(",").some((rawType) => {
    const type = rawType.trim()
    if (!type) return false
    if (type === "application/pdf") {
      return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
    }
    if (type.endsWith("/*")) {
      return file.type.startsWith(type.slice(0, -1))
    }
    return file.type === type
  })
}

const UPLOAD_TYPE = "public"
const SUCCESS_ICON_TIMEOUT = 1000

function fileNameFromUrl(url: string): string {
  const segment = url.split("/").pop() ?? ""
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

export const useFileUpload = ({
  onChange,
  value,
  inReview,
  acceptedFileTypes,
  onInvalidFile,
  onFileSelect,
}: UseFileUploadProps) => {
  const { t } = useTranslation()
  const [fileName, setFileName] = useState<string>("")
  const [upload, { isLoading: isUploading }] = useUploadFileMutation()
  const [uploadSuccessful, setUploadSuccessful] = useState(false)
  const [uploadSuccessfulIcon, setUploadSuccessfulIcon] = useState(false)
  const inputRefBase = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (value && typeof value === "string") {
      setUploadSuccessful(true)
      setFileName(fileNameFromUrl(value))
    } else if (!value) {
      setUploadSuccessful(false)
      setUploadSuccessfulIcon(false)
      setFileName("")
    }
  }, [value])

  useEffect(() => {
    if (inReview) {
      setUploadSuccessful(false)
    }
  }, [inReview])

  useEffect(() => {
    if (!uploadSuccessfulIcon) return
    const timeout = setTimeout(() => setUploadSuccessfulIcon(false), SUCCESS_ICON_TIMEOUT)
    return () => clearTimeout(timeout)
  }, [uploadSuccessfulIcon])

  const handleFileUpload = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      if (!onChange && !onFileSelect) return

      const accept = acceptedFileTypes?.trim()
      if (accept && !isFileAccepted(file, accept)) {
        e.target.value = ""
        onInvalidFile?.()
        return
      }

      setFileName(file.name)

      // Parent-owned upload pipeline: surface the File and skip S3 upload here.
      if (onFileSelect) {
        setUploadSuccessful(false)
        setUploadSuccessfulIcon(false)
        onFileSelect(file)
        e.target.value = ""
        return
      }

      const formData = new FormData()
      formData.append("files", file)

      try {
        const uploadRequest: UploadRequest = {
          files: formData,
          type: UPLOAD_TYPE,
        }
        const response = (await upload(uploadRequest).unwrap()) as UploadResponse

        setUploadSuccessful(true)
        setUploadSuccessfulIcon(true)

        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: response.files[0]?.location ?? "",
          },
        }
        onChange?.(syntheticEvent)
      } catch (error) {
        console.error("Error uploading file:", error)
        Toast({ type: "error", message: t("campaignManager.booking.content.upload.uploadFailed") })
      }
    },
    [upload, onChange, onFileSelect, acceptedFileTypes, onInvalidFile, t],
  )

  const handleRemoveFile = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (!onChange) return

      const syntheticEvent = {
        target: { value: "" },
      } as ChangeEvent<HTMLInputElement>

      onChange(syntheticEvent)
      setUploadSuccessful(false)
      setFileName("")

      // Reset the file input value to allow selecting the same file again
      if (inputRefBase.current) {
        inputRefBase.current.value = ""
      }
    },
    [onChange],
  )

  const handleOpenFile = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (value && typeof value === "string") {
        window.open(value, "_blank", "noopener,noreferrer")
      }
    },
    [value],
  )

  const handleTriggerFileSelect = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    inputRefBase.current?.click()
  }, [])

  return {
    fileName,
    handleFileUpload,
    handleRemoveFile,
    handleOpenFile,
    handleTriggerFileSelect,
    uploadSuccessful,
    uploadSuccessfulIcon,
    isUploading,
    inputRefBase,
    upload,
  }
}
